import { makeAutoObservable, runInAction } from 'mobx'
import { logger } from 'shared/lib'
import type { ITabItem } from 'shared/ui'
import { AiCallingApi } from 'entities/AICalling/api/aiCallingApi'
import type { IResponseGetCallingVoice } from 'entities/AICalling/api/types'
import { cacheStore } from 'entities/Cache/CacheStore'
import { type IResponseVoice, type ISpeechPreview } from 'entities/TextToSpeech'
import type { ITextToSpeechWidgetStoreConfigStore } from 'widgets/TextToSpeech/store/textToSpeechWidgetStore'

type IVoiceType = IResponseGetCallingVoice['gender']

export class GreetingVoiceConfigStore implements ITextToSpeechWidgetStoreConfigStore {
  constructor(
    readonly parent: {
      setSelectedVoice: (voice: IResponseVoice) => void
      selectedVoice: IResponseVoice | null
      initVoiceId?: string
    }
  ) {
    makeAutoObservable(this)
  }

  init = async () => {
    try {
      await this.getVoices()

      runInAction(() => {
        const findVoice = this.parent.initVoiceId && this._allVoicesMap.get(this.parent.initVoiceId)

        if (findVoice) {
          this.activeTabKey = findVoice.gender
          this.parent.setSelectedVoice(findVoice)
        } else {
          const firstMaleVoice = this.maleVoices[0]
          if (firstMaleVoice) this.parent.setSelectedVoice(firstMaleVoice)
        }
      })
    } catch (e) {
      logger.error(e)
    }
  }

  private _allVoicesMap: Map<string, IResponseGetCallingVoice> = new Map()
  getVoices = async () => {
    let voices = cacheStore.getData<IResponseGetCallingVoice[]>('callingVoices')
    if (!voices) {
      const { data } = await AiCallingApi.getVoices()
      cacheStore.setData('callingVoices', data.voices)
      voices = data.voices
    }
    if (voices) {
      runInAction(() => {
        this._maleVoices = []
        this._femaleVoices = []
        this._neutralVoices = []
        this._allVoicesMap.clear()
        voices.forEach((voice) => {
          this._allVoicesMap.set(voice.id, voice)
          if (voice.gender === 'masculine') {
            this._maleVoices.push(voice)
          }
          if (voice.gender === 'feminine') {
            this._femaleVoices.push(voice)
          }
          if (voice.gender === 'gender_neutral') {
            this._neutralVoices.push(voice)
          }
        })
      })
    } else {
      throw new Error('No voices')
    }
  }

  readonly speechPreviewMap: Map<string, ISpeechPreview> = new Map()
  getSpeechPreview = (key: string) => {
    return this.speechPreviewMap.get(key)
  }
  loadAudioSpeech: ITextToSpeechWidgetStoreConfigStore['loadAudioSpeech'] = async ({
    voice_id,
    text,
  }) => {
    if (!voice_id) return
    const speechKey = `${voice_id}_${text.trim()}`
    if (this.speechPreviewMap.get(speechKey)) {
      return
    }
    const { data } = await AiCallingApi.generateGreeting({
      voice_id,
      text,
    })

    const src = URL.createObjectURL(data)
    const preview: ISpeechPreview = {
      src,
      duration: 0,
    }

    runInAction(() => {
      this.speechPreviewMap.set(speechKey, preview)
      const audio = new Audio()

      audio.onloadedmetadata = () => {
        runInAction(() => {
          this.speechPreviewMap.set(speechKey, {
            src,
            duration: audio.duration,
          })
        })
      }
      audio.src = src
    })
  }

  loadingPreviewMap: Map<string, Promise<string>> = new Map()
  voicePreviewMap: Map<string, string> = new Map()
  getVoicePreview = async (id: string) => {
    const promise = this.loadingPreviewMap.get(id)
    if (promise) {
      await promise
    }
    const preview = this.voicePreviewMap.get(id)
    if (preview) return preview
    try {
      const promise = AiCallingApi.getVoicePreview(id).then(({ data }) => URL.createObjectURL(data))
      this.loadingPreviewMap.set(id, promise)
      const src = await promise
      runInAction(() => {
        this.voicePreviewMap.set(id, src)
      })

      return src
    } catch (e) {
      logger.error(e)
    } finally {
      runInAction(() => {
        this.loadingPreviewMap.delete(id)
      })
    }
  }

  onErrorLoadPreview = (id: string) => {
    this.voicePreviewMap.delete(id)
  }

  search = ''
  setSearch = (value: string) => {
    this.search = value
  }

  activeTabKey: IVoiceType = 'masculine'
  setActiveTabKey = (activeTabKey: IVoiceType) => {
    this.activeTabKey = activeTabKey
  }

  private filterVoicesBySearch = (voice: IResponseVoice) =>
    voice.name.toLowerCase().includes(this.search.toLowerCase())

  private _maleVoices: IResponseVoice[] = []
  get maleVoices(): IResponseVoice[] {
    return this._maleVoices.filter(this.filterVoicesBySearch)
  }
  private _femaleVoices: IResponseVoice[] = []
  get femaleVoices(): IResponseVoice[] {
    return this._femaleVoices.filter(this.filterVoicesBySearch)
  }
  private _neutralVoices: IResponseVoice[] = []
  get neutralVoices(): IResponseVoice[] {
    return this._neutralVoices.filter(this.filterVoicesBySearch)
  }

  get tabs(): ITabItem<IVoiceType>[] {
    return [
      {
        key: 'masculine',
        count: this.maleVoices.length,
        name: 'Male',
      },
      {
        key: 'feminine',
        count: this.femaleVoices.length,
        name: 'Female',
      },
      {
        key: 'gender_neutral',
        count: this.neutralVoices.length,
        name: 'Neutral',
      },
    ]
  }

  get voices(): IResponseVoice[] {
    return this.voicesObj[this.activeTabKey]
  }

  private get voicesObj(): { [key in IVoiceType]: IResponseVoice[] } {
    return {
      masculine: this.maleVoices,
      feminine: this.femaleVoices,
      gender_neutral: this.neutralVoices,
    }
  }
}
